<template>
  <el-col :span="24" id="billableitem">
    <el-row type="flex">
      <el-col :span="24">
        <el-row type="flex" justify="start" align="middle" id="billableitem-header">
          <HeaderInfoText :colspan="9" title="BILLABLE ITEMS" :meta="meta" :selection="selection"></HeaderInfoText>
          <el-col :span="6">
            <el-row type="flex" justify="center" v-if="meta.count > 20">
              <el-pagination
                layout="prev, pager, next, jumper"
                :total="meta.count"
                :page-size="20"
                :current-page.sync="currentPage">
              </el-pagination>
            </el-row>
          </el-col>
          <el-col :span="9" class="h-padded right-aligned-col">
            <el-button type="primary" icon="plus" @click="createNewModel"></el-button>
          </el-col>
        </el-row>
        <el-row type="flex" justify="end" align="middle">
          <el-col :span="24">
            <el-table
              v-if="meta.count && !changingPage"
              id="billableitem-table"
              :ref="`${endpoint}Table`"
              :data="pageData"
              border
              style="width: 100%; z-index: 0;"
              @select="handleTableSelectionChange"
              @select-all="handleTableSelectionChange"
              :row-class-name="tableRowClassName">
              <el-table-column prop="code" label="Code" align="center" width="120">
                <template slot-scope="scope">
                  <el-row type="flex" justify="center">
                    <a class="link-mock" @click="editExistingModel(scope.row.id)">
                      {{ scope.row.code }}
                    </a>
                  </el-row>
                </template>
              </el-table-column>
              <el-table-column prop="name" label="Name" align="start">
                <template slot-scope="scope">
                  <el-row type="flex" justify="start">
                    <a class="link-mock" @click="editExistingModel(scope.row.id)">
                      {{ scope.row.name }}
                    </a>
                  </el-row>
                </template>
              </el-table-column>
              <el-table-column prop="default_markup_amount_decimal" label="Default Markup Amount" align="center" width="300">
                <template slot-scope="scope">
                  {{ scope.row.default_markup_amount_decimal ? `$${scope.row.default_markup_amount_decimal}` : '' }}
                </template>
              </el-table-column>
              <el-table-column prop="default_markup_rate" label="Default Markup Rate" align="center" width="300">
                <template slot-scope="scope">
                  {{ scope.row.default_markup_rate ? `${scope.row.default_markup_rate} %` : '' }}
                </template>
              </el-table-column>
            </el-table>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <el-dialog
      :title="editMode.active ? `Edit Billable Item` : 'Create New Billable Item'"
      size="small"
      :visible.sync="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        v-if="editMode.active"
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'"
        :errMsg="errMsg"
        ref="form"
        @formChanged="trackFormChange"
      />
      <NewEntityForm
        v-else
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'"
        :errMsg="errMsg"
        ref="form"
        @formChanged="trackFormChange"
      />
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="saving || !!updating" @click="closeFormModal">Cancel</el-button>
        <el-button :disabled="!!errMsg" :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
      </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Billableitems/actionPayload'
  import store from '@/store/store'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import getUniqueValues from '@/utils/helpers/GetUniqueValues'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { arSystemViewMixins } from '@/Mixins/ArSystemViews'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      NewEntityForm,
      HeaderInfoText
    },
    mixins: [ systemViewMixins, arSystemViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        batchUpdateOptions: [
          { id: 0, text: 'Delete selected Billable Items' }
        ],
        batchUpdateSelection: null,
        selection: [],
        dialogFormVisible: false,
        changingPage: false,
        editMode: { active: false, modelId: null },
        hiddenFields: []
      }
    },
    methods: {
      getUniqueValues,
      /**
       * Tracks whether the form has been changed
       * Note: OVERRIDES METHOD IN SystemViewMixins.js mixin
       */
      trackFormChange (field) {
        this.$store.dispatch('isEditingForm', this.hasBeenChanged)
        const { asset_inventory_account, cogs_account } = this.form
        this.displayAmountOrPercentageFields()
        if (field === 'billable_type' && this.form.billable_type.value) {
          this.autofillAccountFields()
        }
        if (asset_inventory_account.value && cogs_account.value && (asset_inventory_account.value === cogs_account.value)) {
          this.errMsg = 'Asset Inventory Account and Cost-Of-Goods Account cannot be identical.'
        } else {
          this.errMsg = null
        }
      },
      /**
       * Auto-fills all account fields that are in this billable_item form based on their corresponding value in their
       * selected billable_type counterpart. Ignored if a value has already been set.
       */
      autofillAccountFields () {
        const billableType = this.getBillabletypeById(this.form.billable_type.value)
        for (let field in this.form) {
          const name = field.split('_')
          if (name[name.length - 1] !== 'account') continue
          if (this.form[field] && this.form[field].value) continue
          const billableTypeFieldName = `default_${field}`
          if (billableType[billableTypeFieldName]) {
            const acct = this.getAccountById(billableType[billableTypeFieldName])
            this.form[field] = Object.assign({}, { label: acct.name, value: acct.id })
          }
        }
      }
    },
    computed: {
      ...mapGetters({
        getAccountById: 'getAccountById',
        getVendorById: 'getVendorById',
        getBillabletypeById: 'getBillabletypeById'
      }),
      data () {
        return this.sortData(this.$store.getters.getAllBillableitem)
      },
      pageData () {
        return this.meetsFilterCriteria(this.data).slice((this.currentPage - 1) * 20, this.currentPage * 20)
      },
      meta () {
        return { count: this.meetsFilterCriteria(this.data).length, shown: this.pageData.length }
      }
    },
    watch: {
      pageData () {
        if (this.restoreTableSelection && typeof this.restoreTableSelection === 'function') {
          this.restoreTableSelection()
        }
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      let initPromises = []
      const fieldsAlreadyExist = !typeof store.getters.getResourceFieldsByEndpoint(payload.endpoint) === 'undefined'
      if (!fieldsAlreadyExist) {
        initPromises.push(store.dispatch('fetchOptions', payload))
      }
      try {
        await Promise.all(initPromises)
        if (from.path !== '/') {
          let dataLoader = new DataLoader(store, [ payload ], true, 200)
          await dataLoader.loadData()
        }
      } catch (e) {
        console.log(e)
      }
      next()
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
    }
  }
</script>

<style scoped>
  #billableitem-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Billableitems/Billableitems.vue